"use client";
import React, { useState } from "react";
import Link from "next/link";
import Button from "../ui/Button";
import ShareDropdown from "./ShareDropdown";
import DownloadProgramModal from "./DownloadProgramModal";
import { motion, AnimatePresence } from "framer-motion";
import type { IFormationDetail } from "@/types/formation";
import { MODALITY_LABELS } from "@/constants/formation";
import FinanceablePill from "./FinanceablePill";

interface DetailedProgramProps {
  formation: IFormationDetail;
}

/* The three closing CTAs. Full width stacked, then equal flexible columns that
   wrap rather than shrink below a readable label. */
const CTA_CLASS =
  "w-full md:w-auto md:flex-1 md:min-w-52 md:max-w-70 h-14 rounded-lg text-[14px] font-bold px-6 gap-2";

const DetailedProgram: React.FC<DetailedProgramProps> = ({ formation }) => {
  const [openItems, setOpenItems] = useState<number[]>([]);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);

  const toggleItem = (id: number) => {
    setOpenItems((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };
  const isFinanceable = formation.is_financeable;
  const formatLabel = formation.modality?.length
    ? formation.modality.map((m) => MODALITY_LABELS[m] ?? m).join(" ou ")
    : "Présentiel";

  return (
    <section id="programme" className="md:py-10 py-6 scroll-mt-36 lg:scroll-mt-24">
      <div className="bg-white rounded-3xl md:px-16 px-4 md:pt-14 pt-12 md:pb-12 pb-8 border border-[#40BFDE] relative">
        <h2 className="font-red-hat text-[30px] md:text-[40px] leading-10 md:leading-13.25 font-light text-[#1B365E] text-center pb-4">
          Programme détaillé
        </h2>

        <div>
          <div className="w-full flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:gap-2.5 md:items-start items-center relative">
              <div className="sm:flex hidden items-center flex-wrap gap-3.5">
                <Link
                  href={`/formations/${formation.category_slug}`}
                  className="bg-white px-2.5 py-1 rounded-full border border-[#EFEFEF] text-[11px] font-bold leading-5 text-[#1B515E] hover:border-[#40BFDE] transition-colors"
                >
                  {formation.category}
                </Link>
                {isFinanceable && <FinanceablePill />}
                {formation.reference && (
                  <span className="text-[11px] leading-5 text-[#676767]">
                    {formation.reference}
                  </span>
                )}
              </div>
              {/* Secondary to the section's own h2 — the two used to be set at
                  the same 40px, which flattened the hierarchy. */}
              <h3 className="font-red-hat font-light text-[#1B365E] text-[24px] md:text-[30px] leading-8 md:leading-10 text-center md:text-left">
                {formation.title}
              </h3>
              <div className="flex w-full flex-wrap items-center justify-between gap-3 md:gap-4 md:mt-4 md:mb-5 mb-1">
                <div className="flex items-center flex-wrap md:gap-5 gap-3 text-[#40BFDE] text-[12px] leading-5">
                  <span>{formation.program.modules.length} modules</span>
                  <span aria-hidden="true">|</span>
                  <span>{formatLabel}</span>
                  <span aria-hidden="true">|</span>
                  <span className="flex items-center gap-2">
                    <span>{formation.duration_days ?? "—"} jours</span>
                    <span aria-hidden="true">•</span>
                    <span>{formation.duration_hours} heures</span>
                  </span>
                </div>
                {/* sm and up: full label button, in the flow of the meta row */}
                <ShareDropdown
                  formationTitle={formation.title}
                  programmeUrl={formation.program_pdf_url ?? undefined}
                />
              </div>
            </div>
            {formation.program.modules.length > 0 ? (
              formation.program.modules.map((module, index) => {
                const isOpen = openItems.includes(index);
                return (
                  <div
                    key={index}
                    className="bg-[#EFEFEF] rounded-lg overflow-hidden transition-all duration-300 ease-in-out"
                  >
                    <button
                      type="button"
                      id={`module-header-${index}`}
                      aria-expanded={isOpen}
                      aria-controls={`module-panel-${index}`}
                      className="w-full flex justify-between items-center gap-4 px-4 lg:px-6 py-3 sm:py-2.5 bg-transparent border-none cursor-pointer text-left transition-colors duration-200 ease-in-out hover:bg-[#D9D9D9]"
                      onClick={() => toggleItem(index)}
                    >
                      <span className="text-[#1D1D1D] text-[13px] sm:text-[16px] leading-5 font-bold tracking-[0.3px]">
                        MODULE {index + 1} - {module.title}
                        {module.duration ? ` (${module.duration})` : ""}
                      </span>
                      {/* leading has to clear the glyph: 16px on a 25px "+" made
                          it overflow its own line box on mobile. */}
                      <motion.span
                        aria-hidden="true"
                        animate={{ rotate: isOpen ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="shrink-0 text-[25px] md:text-[50px] lg:text-[58px] leading-7 md:leading-11.25 font-red-hat font-semibold text-[#1D1D1D] inline-block h-fit"
                      >
                        {isOpen ? "−" : "+"}
                      </motion.span>
                    </button>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          id={`module-panel-${index}`}
                          role="region"
                          aria-labelledby={`module-header-${index}`}
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 lg:px-6 lg:pt-2.5 pb-3 sm:pb-5">
                            <p className="text-[#1D1D1D] text-[14px] leading-6 whitespace-pre-line">
                              {module.content || "Aucun contenu renseigné."}
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })
            ) : (
              <p className="text-[#1D1D1D] text-sm text-center py-6">Programme non renseigné.</p>
            )}
            {/* One size for all three: the first button was left on the default
                `md` size, so it rendered a different height, text size and
                padding from its neighbours — and its 40px side padding pushed
                the row past the card at md. */}
            <div className="flex flex-col md:flex-row md:flex-wrap justify-center items-stretch gap-4 md:gap-6 md:mt-12 mt-8">
              {formation.program_pdf_url && (
                <Button
                  href={formation.program_pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outline-gradient"
                  size="unset"
                  className={CTA_CLASS}
                >
                  <svg
                    className="shrink-0"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4 0C2.89688 0 2 0.896875 2 2V14C2 15.1031 2.89688 16 4 16H12C13.1031 16 14 15.1031 14 14V5H10C9.44688 5 9 4.55312 9 4V0H4ZM10 0V4H14L10 0ZM8.75 7.25V10.4406L9.71875 9.47188C10.0125 9.17813 10.4875 9.17813 10.7781 9.47188C11.0687 9.76562 11.0719 10.2406 10.7781 10.5312L8.52812 12.7812C8.23438 13.075 7.75938 13.075 7.46875 12.7812L5.21875 10.5312C4.925 10.2375 4.925 9.7625 5.21875 9.47188C5.5125 9.18125 5.9875 9.17813 6.27812 9.47188L7.24687 10.4406V7.25C7.24687 6.83437 7.58125 6.5 7.99687 6.5C8.4125 6.5 8.74687 6.83437 8.74687 7.25H8.75Z"
                      fill="#FC7F16"
                    />
                  </svg>
                  Télécharger le programme
                </Button>
              )}
              <Button
                href="#sessions"
                variant="gradient"
                size="unset"
                className={CTA_CLASS}
              >
                S’inscrire à la formation
              </Button>
              <Button
                href="#devis"
                title="Demander un devis"
                variant="outline-gradient"
                size="unset"
                className={CTA_CLASS}
              >
                Demander un devis
              </Button>
            </div>

            <DownloadProgramModal
              isOpen={isDownloadOpen}
              onClose={() => setIsDownloadOpen(false)}
              programName={formation.title}
              formationSlug={formation.slug}
              programPdfUrl={formation.program_pdf_url}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DetailedProgram;
